import { CreditCardIcon } from '@phosphor-icons/react'
import { endOfDay, startOfDay, subDays } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  ReservationAnalytics,
  useGetReservationAnalytics,
} from '@/api/reservations/get-reservation-analytics'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ } from '@/constants/date'
import { DateRange } from '@/models/helpers'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import { getReservationsAnalyticsPeriodOptions } from '../reservations-dashboard/reservations-dashboard.constants'
import { getBuildingToday } from '../reservations-dates.utils'
import { getReservationsAnalyticsMetrics } from './reservations-analytics.constants'
import * as S from './reservations-analytics.styled'

const DEFAULT_DAYS_RANGE = 30
const METRICS_PER_ROW = 5

interface ReservationsAnalyticsProps {
  isOpen: boolean
}

export const ReservationsAnalytics = ({ isOpen }: ReservationsAnalyticsProps) => {
  const { t } = useTranslation(['reservations'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = getBuildingToday(timezone)
    return [subDays(today, DEFAULT_DAYS_RANGE), today]
  })
  const [fromDate, toDate] = dateRange
  // Note: cleared (both null) = all dates; a half-picked range (only the start) waits for the end.
  const isRangeSettled = !fromDate === !toDate

  const { data: analytics, isFetching } = useGetReservationAnalytics({
    buildingId: activeBuilding?.id,
    dateEnd: toDate ? fromZonedTime(endOfDay(toDate), timezone).getTime() : undefined,
    dateStart: fromDate ? fromZonedTime(startOfDay(fromDate), timezone).getTime() : undefined,
    enabled: isOpen && isRangeSettled,
  })

  const metricRows = useMemo(() => {
    const metrics = getReservationsAnalyticsMetrics(t).map((metric) => ({
      ...metric,
      value: metric.getValue(analytics ?? ({} as ReservationAnalytics)),
    }))

    return [metrics.slice(0, METRICS_PER_ROW), metrics.slice(METRICS_PER_ROW)]
  }, [analytics, t])

  return (
    <S.Card>
      <S.AnalyticsHeader align="center" justify="space-between">
        <Flex align="center" gap={8}>
          <Icon icon={CreditCardIcon} size="1.8rem" color="primary500" />
          <Typography size="heading-small" weight="semibold" color="black800">
            {t('reservations:analytics.title')}
          </Typography>
        </Flex>
        <S.DatePickerWrapper>
          <DatePickerInput
            mode="range"
            periodOptions={getReservationsAnalyticsPeriodOptions(t, timezone)}
            placeholder={t('reservations:shared.datePicker.placeholder')}
            size="small"
            value={dateRange}
            onRangeChange={setDateRange}
          />
        </S.DatePickerWrapper>
      </S.AnalyticsHeader>

      <S.MetricsGroup>
        {metricRows.map((row, rowIndex) => (
          <S.MetricsRow key={rowIndex}>
            {row.map((metric) => (
              <S.MetricCard key={metric.label}>
                <Flex align="center" gap={6}>
                  <Icon icon={metric.icon} size={metric.iconSize ?? '1.6rem'} color="black500" />
                  <Typography size="text-small" weight="medium" color="black500">
                    {metric.label}
                  </Typography>
                </Flex>
                {isFetching ? (
                  <S.ValueSkeletonWrapper>
                    <Skeleton variant="text" height="2.4rem" />
                  </S.ValueSkeletonWrapper>
                ) : (
                  <Typography size="heading-huge" weight="semibold" color="black800">
                    {metric.value}
                  </Typography>
                )}
              </S.MetricCard>
            ))}
          </S.MetricsRow>
        ))}
      </S.MetricsGroup>
    </S.Card>
  )
}
